import { useMemo, useState } from 'react'
import { useLocation } from '@reach/router'
import moment from 'moment'
import {
  ArrowButton,
  Button,
  Card,
  DatePicker,
  Popover,
  Segmented,
  showMessage,
} from 'components/common'
import { BaseCardProps } from './types'
import { StatusSegmentedTitle } from 'lib/constants'
import {
  useReportBookingStatusOfDayMutation,
  useReportBookingStatusOfMonthMutation,
} from 'lib/graphql/storeadmin'
import { downloadFile } from 'lib/common'

export interface DayStatusCardProps extends BaseCardProps {
  dailyView?: boolean
  viewDay: moment.Moment
  handleDailyView: (day: moment.Moment) => void
  onChangeToDaily?: (data: boolean) => void
}

export const DayStatusCard = ({
  children,
  dailyView,
  onChangeToDaily,
  viewDay,
  handleDailyView,
}: DayStatusCardProps): JSX.Element => {
  const [reportBookingStatusOfDay, { loading, data, error }] =
    useReportBookingStatusOfDayMutation()
  const [
    reportBookingStatusOfMonth,
    { loading: monthLoading, data: monthData, error: monthError },
  ] = useReportBookingStatusOfMonthMutation()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const handleUpDownDay = (step: boolean) => {
    if (dailyView) {
      if (step) {
        handleDailyView(viewDay.add(1, 'days').clone())
      } else {
        handleDailyView(viewDay.subtract(1, 'days').clone())
      }
    } else {
      if (step) {
        handleDailyView(viewDay.add(1, 'month').clone())
      } else {
        handleDailyView(viewDay.subtract(1, 'month').clone())
      }
    }
  }

  const onChangeSegmented = (value) => {
    if (value === StatusSegmentedTitle.daily) {
      onChangeToDaily(true)
      handleDailyView(moment())
    } else {
      onChangeToDaily(false)
      handleDailyView(moment())
    }
  }
  const content = useMemo<JSX.Element>(
    () => (
      <DatePicker
        value={viewDay}
        onChange={(value) => {
          // Even the return type is [moment.Moment, moment.Moment], its giving Type error
          // eslint-disable-next-line  @typescript-eslint/ban-ts-comment
          // @ts-ignore
          handleDailyView(value)
          setIsOpen(false)
        }}
        allowClear={false}
        picker={dailyView ? 'date' : 'month'}
      />
    ),
    [viewDay, handleDailyView, setIsOpen, dailyView]
  )
  const month = viewDay.format('YYYY-MM-DD')
  const variablesInput = {
    month,
  }

  // 予約状況から日次,月次を出力するAPI呼び出し関数
  const reportBookingStatusMutation = async (value) => {
    try {
      if (value) {
        const { data } = await reportBookingStatusOfDay({
          variables: {
            date: viewDay.format('YYYY-MM-DD'),
          },
        })

        const url = data?.reportBookingStatusOfDay?.url
        const errors = data?.reportBookingStatusOfDay?.reportUserErrors

        if (url) {
          downloadFile(url)
          showMessage('success', '利用予定表を出力しました')
        } else if (errors) {
          showMessage('error', '利用予定表を出力に失敗しました')
        }
      } else {
        const { data: monthData } = await reportBookingStatusOfMonth({
          variables: { input: variablesInput },
        })

        const url = monthData?.reportBookingStatusOfMonth?.url
        const errors = monthData?.reportBookingStatusOfMonth?.reportUserErrors

        if (url) {
          downloadFile(url)
          showMessage('success', '空き状況一覧を出力しました')
        } else if (errors) {
          showMessage('error', '空き状況一覧を出力に失敗しました')
        }
      }
    } catch (err) {
      console.log(err)
      showMessage('error', '出力処理でエラーが発生しました')
    }
  }

  const location = useLocation()
  const showExportButton = !(location.pathname as string).startsWith(
    '/calendar'
  )

  return (
    <>
      <Card
        title={
          <Segmented
            options={[StatusSegmentedTitle.monthly, StatusSegmentedTitle.daily]}
            defaultValue={StatusSegmentedTitle.monthly}
            value={
              dailyView
                ? StatusSegmentedTitle.daily
                : StatusSegmentedTitle.monthly
            }
            onChange={onChangeSegmented}
          />
        }
        extra={
          <div className="flex items-center">
            {/* エクスポートするボタンを押すと、日次月次ダウンロードAPIを呼び出す */}
            {showExportButton && (
              <Button
                type="text"
                onClick={() => {
                  reportBookingStatusMutation(dailyView)
                }}
              >
                エクスポートする
              </Button>
            )}
            <Button.Group>
              <ArrowButton
                direction="left"
                onClick={() => handleUpDownDay(false)}
              />

              <Popover
                title={'日付を選択'}
                open={isOpen}
                onOpenChange={setIsOpen}
                trigger="click"
                content={content}
              >
                <Button onClick={() => setIsOpen(true)}>
                  {dailyView
                    ? viewDay.format('YYYY年MM月DD日')
                    : viewDay.format('YYYY年MM月')}
                </Button>
              </Popover>

              <ArrowButton
                direction="right"
                onClick={() => handleUpDownDay(true)}
              />
            </Button.Group>
          </div>
        }
      >
        {children}
      </Card>
    </>
  )
}
